import { useEffect, useRef, useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  Calculator,
  CircleDollarSign,
  GitCompareArrows,
  ShieldAlert,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Toaster, toast } from "sonner";

import DonutSplitLayout from "@/components/ui/DonutSplitLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import KpiStatCard from "@/components/ui/KpiStatCard";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_KPI_REGISTRY } from "@/admin/config/adminKpiNaming.ts";
import { getApiErrorMessage } from "@/lib/api";
import { hasRequiredRole } from "@/lib/adminRoles";
import {
  DEFAULT_BILLABLE_DAYS_PER_MONTH,
  DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
  normalizePctInputToRate,
  parseLocaleNumber,
  resolveIntermediairePct,
} from "@/lib/businessRules";
import { computeSimulation, getSimulationDefaults } from "@/lib/pilotageApi";
import { computeSimulationSandboxPreview } from "@/lib/ui/simulationSandboxPreview";
import {
  CONSULTANT_RATE_LABEL,
  INTERMEDIARY_MARGIN_LABEL,
  DEFAULT_EMPLOYER_COST_COEFFICIENT,
  TACE_LABEL,
  formatEUR0,
  formatNumber,
  formatPercentInputValue,
  formatPct0,
  parsePercentInputToRatio,
  ratioToPercent,
} from "@/lib/pilotageUi";
import { TESTIDS } from "@/lib/testids";

const AUTO_COMPUTE_DELAY = 320;
const LOCAL_DEFAULTS = {
  days_per_month: DEFAULT_BILLABLE_DAYS_PER_MONTH,
  tace: 0.8,
  intermediary_pct: DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
};
const SANDBOX_INPUTS = {
  FREELANCE: {
    tarif_client: 700,
    days_per_month: LOCAL_DEFAULTS.days_per_month,
    intermediary_enabled: false,
    intermediary_pct: ratioToPercent(LOCAL_DEFAULTS.intermediary_pct),
    projection_months: 12,
    tjm_consultant: 500,
    salaire_annuel: 52000,
    tace: LOCAL_DEFAULTS.tace,
  },
  CDI: {
    tarif_client: 650,
    days_per_month: LOCAL_DEFAULTS.days_per_month,
    intermediary_enabled: false,
    intermediary_pct: ratioToPercent(LOCAL_DEFAULTS.intermediary_pct),
    projection_months: 12,
    tjm_consultant: 500,
    salaire_annuel: 52000,
    tace: LOCAL_DEFAULTS.tace,
  },
};
const CHART_COLORS = {
  cost: "#F97316",
  intermediary: "#F59E0B",
  deficit: "#EF4444",
  margin: "#22C55E",
};

const formatSimulationCurrency = (value) =>
  formatEUR0(value);
const formatSimulationPercent = (value) =>
  formatPct0(value);

const clampNumber = (value, fallback = 0) => {
  const numericValue = parseLocaleNumber(value);
  return numericValue === null ? fallback : numericValue;
};

const clampRange = (value, min, max, fallback) => {
  const numericValue = clampNumber(value, fallback);
  return Math.min(max, Math.max(min, numericValue));
};

const computeCdiDailyCost = (annualSalary) =>
  (clampNumber(annualSalary, SANDBOX_INPUTS.CDI.salaire_annuel) * DEFAULT_EMPLOYER_COST_COEFFICIENT) / 210;

const deriveCdiInternalDailyCost = (result, annualSalary) => {
  const billedDays = clampNumber(result?.monthly?.billed_days, 0);
  const monthlyCost = clampNumber(result?.monthly?.cost, 0);

  if (billedDays > 0 && monthlyCost > 0) {
    return {
      value: monthlyCost / billedDays,
      isEstimated: false,
    };
  }

  return {
    value: computeCdiDailyCost(annualSalary),
    isEstimated: true,
  };
};

const applyDefaultsToForms = (defaults = LOCAL_DEFAULTS) => {
  const sanitizedDefaults = {
    days_per_month: clampNumber(defaults.days_per_month, LOCAL_DEFAULTS.days_per_month),
    tace: clampRange(defaults.tace, 0, 1, LOCAL_DEFAULTS.tace),
    intermediary_pct: resolveIntermediairePct(null, {
      defaultIntermediairePct: defaults.intermediary_pct,
      treatDefaultZeroAsMissing: true,
    }),
  };

  return {
    FREELANCE: {
      ...SANDBOX_INPUTS.FREELANCE,
      days_per_month: sanitizedDefaults.days_per_month,
      intermediary_pct: formatPercentInputValue(sanitizedDefaults.intermediary_pct),
    },
    CDI: {
      ...SANDBOX_INPUTS.CDI,
      days_per_month: sanitizedDefaults.days_per_month,
      intermediary_pct: formatPercentInputValue(sanitizedDefaults.intermediary_pct),
      tace: sanitizedDefaults.tace,
    },
  };
};

const buildPayload = (mode, form, { includeSensitive = true } = {}) => {
  const defaultIntermediaryPercent = ratioToPercent(DEFAULT_INTERMEDIAIRE_MARGIN_PCT) ?? 0;
  const payload = {
    mode,
    tarif_client: Math.max(0, clampNumber(form.tarif_client, SANDBOX_INPUTS[mode].tarif_client)),
    days_per_month: Math.max(0, clampNumber(form.days_per_month, LOCAL_DEFAULTS.days_per_month)),
    intermediary_pct: form.intermediary_enabled
      ? normalizePctInputToRate(
        clampRange(form.intermediary_pct, 0, 100, defaultIntermediaryPercent),
        DEFAULT_INTERMEDIAIRE_MARGIN_PCT,
      ) ?? DEFAULT_INTERMEDIAIRE_MARGIN_PCT
      : 0,
    projection_months: clampRange(form.projection_months, 1, 12, 12),
    tace: clampRange(form.tace, 0, 1, LOCAL_DEFAULTS.tace),
  };

  if (mode === "FREELANCE") {
    return {
      ...payload,
      tjm_consultant: Math.max(0, clampNumber(form.tjm_consultant, SANDBOX_INPUTS.FREELANCE.tjm_consultant)),
      salaire_annuel: null,
    };
  }

  if (includeSensitive) {
    return {
      ...payload,
      tjm_consultant: null,
      salaire_annuel: Math.max(0, clampNumber(form.salaire_annuel, SANDBOX_INPUTS.CDI.salaire_annuel)),
    };
  }

  return {
    ...payload,
    tjm_consultant: null,
  };
};

const computeSandboxResult = (payload) => {
  const mode = payload.mode === "CDI" ? "CDI" : "FREELANCE";
  const costPerDay = mode === "CDI"
    ? computeCdiDailyCost(payload.salaire_annuel)
    : clampNumber(payload.tjm_consultant, SANDBOX_INPUTS.FREELANCE.tjm_consultant);

  return computeSimulationSandboxPreview({
    mode,
    tarifClient: Math.max(0, clampNumber(payload.tarif_client, 0)),
    daysPerMonth: Math.max(0, clampNumber(payload.days_per_month, LOCAL_DEFAULTS.days_per_month)),
    projectionMonths: clampRange(payload.projection_months, 1, 12, 12),
    intermediaryPct: clampRange(payload.intermediary_pct, 0, 1, 0),
    tace: clampRange(payload.tace, 0, 1, LOCAL_DEFAULTS.tace),
    costPerDay,
    salaireAnnuel: payload.salaire_annuel,
    tjmConsultant: payload.tjm_consultant,
  });
};

const Field = ({
  label,
  value,
  onChange,
  disabled = false,
  type = "number",
  inputMode,
  step = "1",
  min = "0",
  max,
  suffix,
  testId,
}) => (
  <label className="block" data-testid={testId}>
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] leading-relaxed text-slate-500">
      {label}
    </span>
    <div className="relative">
      <Input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        disabled={disabled}
        step={step}
        min={min}
        max={max}
        className="h-11 rounded-2xl border-white/10 bg-slate-950/40 pr-12 text-white"
      />
      {suffix ? (
        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-slate-500">
          {suffix}
        </span>
      ) : null}
    </div>
  </label>
);

const ChartCard = ({ title, description, icon: Icon, children, testId }) => (
  <section
    className="min-w-0 rounded-3xl border border-white/10 glass p-6"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <p className="mt-1 text-sm text-slate-400">{description}</p>
      </div>
      {Icon ? (
        <div className="rounded-2xl bg-lenvia-blue/10 p-3 text-lenvia-blue">
          <Icon className="h-5 w-5" />
        </div>
      ) : null}
    </div>
    <div className="mt-5 min-w-0">{children}</div>
  </section>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const title = label || payload[0]?.name;

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/95 p-3 shadow-xl">
      {title ? <p className="text-sm font-medium text-white">{title}</p> : null}
      {payload.map((item) => (
        <p key={item.dataKey} className="mt-1 text-sm" style={{ color: item.color }}>
          {item.name}: {formatSimulationCurrency(item.value)}
        </p>
      ))}
    </div>
  );
};

const buildDonutData = (breakdown) => {
  const netProfit = clampNumber(breakdown?.net_profit, 0);

  return [
    {
      name: breakdown?.cost_label || "Consultant",
      value: Math.max(0, clampNumber(breakdown?.cost, 0)),
      color: CHART_COLORS.cost,
    },
    {
      name: "Intermediaire",
      value: Math.max(0, clampNumber(breakdown?.intermediary, 0)),
      color: CHART_COLORS.intermediary,
    },
    {
      name: netProfit >= 0 ? "Marge nette" : "Deficit",
      value: netProfit >= 0 ? Math.max(0, netProfit) : Math.max(0, clampNumber(breakdown?.deficit, 0)),
      color: netProfit >= 0 ? CHART_COLORS.margin : CHART_COLORS.deficit,
    },
  ].filter((item) => item.value > 0);
};

const resolveBreakdown = (result, mode) => {
  if (result?.breakdown) {
    return result.breakdown;
  }

  const netProfit = clampNumber(result?.monthly?.profit, result?.benefit_monthly_net);

  return {
    mode: mode === "CDI" ? "CDI" : "FREELANCE",
    cost_label: mode === "CDI" ? "Salaire charge" : "Consultant",
    cost: clampNumber(result?.monthly?.cost, 0),
    intermediary: clampNumber(
      result?.monthly?.intermediary_loss,
      result?.intermediary_impact_monthly,
    ),
    gross_margin: clampNumber(result?.monthly?.profit_without_intermediary, 0),
    net_profit: netProfit,
    deficit: netProfit < 0 ? Math.abs(netProfit) : 0,
  };
};

const buildModePayloadForForms = (mode, sourceForms, isAdmin) =>
  buildPayload(mode, sourceForms[mode], { includeSensitive: isAdmin });

const buildModeFallbackResult = (mode, sourceForms, isAdmin) =>
  computeSandboxResult(buildModePayloadForForms(mode, sourceForms, isAdmin));

const setModeResult = (mode, result, setResults) => {
  setResults((currentResults) => ({
    ...currentResults,
    [mode]: result,
  }));
};

const startSimulationCompute = ({
  mode,
  sourceForms,
  isAdmin,
  setResults,
  setIsComputing,
  setComputeErrorMessage,
  requestRef,
  timerRef,
  delay = AUTO_COMPUTE_DELAY,
}) => {
  const payload = buildModePayloadForForms(mode, sourceForms, isAdmin);

  setModeResult(mode, computeSandboxResult(payload), setResults);
  setComputeErrorMessage("");

  if (timerRef.current) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }

  requestRef.current.controller?.abort();

  const requestId = requestRef.current.id + 1;
  const controller = new AbortController();
  requestRef.current = {
    id: requestId,
    controller,
  };

  setIsComputing(true);

  const runCompute = async () => {
    try {
      const computedResult = await computeSimulation(payload, {
        signal: controller.signal,
      });

      if (controller.signal.aborted || requestRef.current.id !== requestId) {
        return;
      }

      setModeResult(mode, computedResult, setResults);
      setComputeErrorMessage("");
    } catch (error) {
      if (controller.signal.aborted || requestRef.current.id !== requestId) {
        return;
      }

      setComputeErrorMessage(
        getApiErrorMessage(
          error,
          "Le calcul distant a echoue. Le resultat sandbox local est conserve.",
        ),
      );
    } finally {
      if (requestRef.current.id === requestId) {
        requestRef.current = {
          id: requestId,
          controller: null,
        };
        setIsComputing(false);
      }
    }
  };

  if (delay <= 0) {
    void runCompute();
    return;
  }

  timerRef.current = window.setTimeout(() => {
    timerRef.current = null;
    void runCompute();
  }, delay);
};

const SimulationPage = () => {
  const { role } = useAuth();
  const isAdmin = hasRequiredRole(role, ["ADMIN"]);
  const isRh = role === "RH";

  const [activeMode, setActiveMode] = useState("FREELANCE");
  const [forms, setForms] = useState(() => applyDefaultsToForms());
  const [results, setResults] = useState(() => {
    const initialForms = applyDefaultsToForms();

    return {
      FREELANCE: buildModeFallbackResult("FREELANCE", initialForms, isAdmin),
      CDI: buildModeFallbackResult("CDI", initialForms, isAdmin),
    };
  });
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [hasHydratedDefaults, setHasHydratedDefaults] = useState(false);
  const [isComputing, setIsComputing] = useState(false);
  const [defaultsErrorMessage, setDefaultsErrorMessage] = useState(null);
  const [computeErrorMessage, setComputeErrorMessage] = useState("");
  const defaultSettingsRef = useRef(LOCAL_DEFAULTS);
  const computeTimerRef = useRef(null);
  const computeRequestRef = useRef({
    id: 0,
    controller: null,
  });

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setHasHydratedDefaults(false);
      setIsBootstrapping(true);
      setDefaultsErrorMessage(null);

      let nextDefaults = LOCAL_DEFAULTS;

      try {
        const apiDefaults = await getSimulationDefaults();
        nextDefaults = apiDefaults;
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          "Impossible de charger les parametres par defaut. Mode sandbox local active.",
        );
        if (!cancelled) {
          setDefaultsErrorMessage(message);
          toast.error(message);
        }
      }

      if (cancelled) {
        return;
      }

      defaultSettingsRef.current = nextDefaults;
      const nextForms = applyDefaultsToForms(nextDefaults);
      setForms(nextForms);
      setResults({
        FREELANCE: buildModeFallbackResult("FREELANCE", nextForms, isAdmin),
        CDI: buildModeFallbackResult("CDI", nextForms, isAdmin),
      });
      setComputeErrorMessage("");
      setHasHydratedDefaults(true);
      setIsBootstrapping(false);
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!hasHydratedDefaults) {
      return undefined;
    }

    startSimulationCompute({
      mode: activeMode,
      sourceForms: forms,
      isAdmin,
      setResults,
      setIsComputing,
      setComputeErrorMessage,
      requestRef: computeRequestRef,
      timerRef: computeTimerRef,
    });

    return () => {
      if (computeTimerRef.current) {
        window.clearTimeout(computeTimerRef.current);
        computeTimerRef.current = null;
      }

      const cancelledRequestId = computeRequestRef.current.id + 1;
      computeRequestRef.current.controller?.abort();
      computeRequestRef.current = {
        id: cancelledRequestId,
        controller: null,
      };
    };
  }, [activeMode, forms, hasHydratedDefaults, isAdmin]);

  const updateModeForm = (mode, field, value) => {
    setForms((currentForms) => ({
      ...currentForms,
      [mode]: {
        ...currentForms[mode],
        [field]: value,
      },
    }));
  };

  const updateNumericModeForm = (mode, field, rawValue) => {
    if (rawValue === "") {
      updateModeForm(mode, field, "");
      return;
    }

    updateModeForm(mode, field, Math.max(0, clampNumber(rawValue, 0)));
  };

  const updatePercentModeForm = (mode, field, rawValue) => {
    if (rawValue === "") {
      updateModeForm(mode, field, "");
      return;
    }

    updateModeForm(mode, field, rawValue);
  };

  const runImmediateCompute = () => {
    startSimulationCompute({
      mode: activeMode,
      sourceForms: forms,
      isAdmin,
      setResults,
      setIsComputing,
      setComputeErrorMessage,
      requestRef: computeRequestRef,
      timerRef: computeTimerRef,
      delay: 0,
    });
  };

  const activeForm = forms[activeMode];
  const activeResult = results[activeMode];
  const donutData = buildDonutData(resolveBreakdown(activeResult, activeMode));
  const isIntermediaryEnabled = Boolean(activeForm?.intermediary_enabled);
  const intermediaryPercentValue = clampRange(
    activeForm?.intermediary_pct,
    0,
    100,
    ratioToPercent(DEFAULT_INTERMEDIAIRE_MARGIN_PCT) ?? 0,
  );
  const hasIntermediaryImpact = isIntermediaryEnabled && intermediaryPercentValue > 0;
  const cdiInternalDailyCost =
    activeMode === "CDI" && isAdmin
      ? deriveCdiInternalDailyCost(activeResult, activeForm?.salaire_annuel)
      : null;

  const financeCards = [
    {
      label: ADMIN_KPI_REGISTRY.ca_mensuel.label,
      value: formatSimulationCurrency(activeResult?.ca_monthly_net),
      hint: `${formatNumber(activeResult?.monthly?.billed_days || 0, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} j facturés / mois`,
      icon: CircleDollarSign,
      dataTestId: TESTIDS.simulation.kpiCaMensuel,
    },
    {
      label: ADMIN_KPI_REGISTRY.ca_annuel.label,
      value: formatSimulationCurrency(activeResult?.ca_annual_net),
      hint: "Base annuelle a rythme constant",
      icon: TrendingUp,
      dataTestId: TESTIDS.simulation.kpiCaYear,
    },
  ];

  if (isAdmin) {
    financeCards.push(
      {
        label: "Benefice annuel",
        value: formatSimulationCurrency(activeResult?.benefit_annual_net),
        hint: `${formatSimulationCurrency(activeResult?.benefit_monthly_net)} / mois`,
        icon: Calculator,
        dataTestId: TESTIDS.simulation.kpiProfitYear,
      },
      {
        label: "Marge moyenne",
        value: formatSimulationPercent(activeResult?.margin_pct_net),
        hint: hasIntermediaryImpact ? "Après impact intermédiaire" : "Sans impact intermédiaire",
        icon: GitCompareArrows,
        dataTestId: TESTIDS.simulation.kpiMargin,
      },
    );
  }

  return (
    <div className="relative px-4 py-6 lg:px-6" data-testid={TESTIDS.simulation.container}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-lenvia-blue/10 to-transparent" />

      <div className="relative space-y-6">
        <section className="rounded-3xl border border-white/10 glass p-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-lenvia-blue">
                Recrutement intelligent
              </p>
              <h1 className="mt-3 text-3xl font-semibold text-white">Simulation de recrutement</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-400">
                Estimation rapide du CA, du benefice et de la marge avant affectation
                en mission ou positionnement candidat.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-right">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Etat
                </p>
                <p className="mt-1 text-sm font-medium text-white" data-testid={TESTIDS.simulation.computeStatus}>
                  {isBootstrapping ? "Chargement..." : isComputing ? "Calcul..." : "Pret"}
                </p>
              </div>
              <div className="rounded-2xl border border-lenvia-blue/20 bg-lenvia-blue/10 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lenvia-blue">
                  Mode
                </p>
                <p className="mt-1 text-sm font-medium text-white">
                  {isAdmin ? "Administration complete" : "Lecture RH"}
                </p>
              </div>
            </div>
          </div>

          {defaultsErrorMessage ? (
            <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              {defaultsErrorMessage}
            </div>
          ) : null}

          {computeErrorMessage ? (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              <span>{computeErrorMessage}</span>
              {!isRh ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={runImmediateCompute}
                  disabled={isBootstrapping || isComputing}
                  className="h-8 rounded-xl border border-amber-200/20 px-3 text-amber-50 hover:bg-amber-200/10 hover:text-white"
                  data-testid={TESTIDS.simulation.retryButton}
                >
                  Recalculer
                </Button>
              ) : null}
            </div>
          ) : null}

          {isRh ? (
            <div
              className="mt-5 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300"
              data-testid={TESTIDS.simulation.readOnlyBanner}
            >
              <ShieldAlert className="mt-0.5 h-4 w-4 flex-none text-lenvia-blue" />
              <span>
                Acces lecture seule. Les hypotheses de cout restent masquees pour le role RH.
              </span>
            </div>
          ) : null}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
          <div className="rounded-3xl border border-white/10 glass p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white">Hypotheses</h2>
                <p className="mt-1 text-sm text-slate-400">
                  Une estimation par statut, avec recalcul automatique et fallback local.
                </p>
              </div>
              <div className="rounded-2xl bg-lenvia-blue/10 p-3 text-lenvia-blue">
                <Calculator className="h-5 w-5" />
              </div>
            </div>

            <Tabs value={activeMode} onValueChange={setActiveMode} className="mt-5">
              <TabsList className="grid h-auto w-full grid-cols-2 rounded-2xl bg-slate-950/50 p-1">
                <TabsTrigger
                  value="FREELANCE"
                  className="rounded-2xl py-2.5 text-slate-300 data-[state=active]:bg-lenvia-blue/15 data-[state=active]:text-white"
                  data-testid={TESTIDS.simulation.modeFreelance}
                >
                  Freelance
                </TabsTrigger>
                <TabsTrigger
                  value="CDI"
                  className="rounded-2xl py-2.5 text-slate-300 data-[state=active]:bg-lenvia-blue/15 data-[state=active]:text-white"
                  data-testid={TESTIDS.simulation.modeCdi}
                >
                  CDI
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="mt-5 grid gap-4">
              {!isRh && (
                <Field
                  label="Tarif auquel il est vendu au client (TJM)"
                  value={activeForm.tarif_client}
                  onChange={(event) =>
                    updateNumericModeForm(activeMode, "tarif_client", event.target.value)
                  }
                  disabled={isBootstrapping}
                  suffix="€/j"
                  testId={TESTIDS.simulation.fieldTjm}
                />
              )}

              <Field
                label="Jours / mois"
                value={activeForm.days_per_month}
                onChange={(event) =>
                  updateNumericModeForm(activeMode, "days_per_month", event.target.value)
                }
                disabled={isBootstrapping}
                suffix="j"
              />

              {activeMode === "FREELANCE" ? (
                isAdmin ? (
                  <Field
                    label={`Tarif d'achat du consultant (${CONSULTANT_RATE_LABEL})`}
                    value={activeForm.tjm_consultant}
                    onChange={(event) =>
                      updateNumericModeForm(activeMode, "tjm_consultant", event.target.value)
                    }
                    disabled={isBootstrapping}
                    suffix="€/j"
                    testId={TESTIDS.simulation.fieldTjm}
                  />
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center">
                    <p className="text-sm font-medium italic text-slate-500">
                      Hypothese de cout freelance masquee
                    </p>
                  </div>
                )
              ) : isAdmin ? (
                <>
                  <Field
                    label="Salaire annuel brut"
                    value={activeForm.salaire_annuel}
                    onChange={(event) =>
                      updateNumericModeForm(activeMode, "salaire_annuel", event.target.value)
                    }
                    disabled={isBootstrapping}
                    suffix="€"
                    testId={TESTIDS.simulation.fieldSalaire}
                  />
                  <div
                    className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                    data-testid={TESTIDS.simulation.cdiDailyCost}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Coût journalier (TJM interne)
                    </p>
                    <div className="mt-1 flex items-baseline gap-2">
                      <p className="text-lg font-semibold text-white">
                        {formatSimulationCurrency(cdiInternalDailyCost?.value)}
                      </p>
                      {cdiInternalDailyCost?.isEstimated ? (
                        <span className="text-[10px] font-medium text-amber-400/80">
                          Basé sur salaire annuel
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium text-lenvia-blue">
                          Reel (via mission)
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center">
                  <p className="text-sm font-medium italic text-slate-500">
                    Hypothese salariale masquee
                  </p>
                </div>
              )}

              {activeMode === "CDI" && (
                <Field
                  label={TACE_LABEL}
                  value={formatPercentInputValue(activeForm.tace)}
                  onChange={(event) =>
                    updateModeForm(
                      activeMode,
                      "tace",
                      event.target.value === ""
                        ? ""
                        : clampRange(parsePercentInputToRatio(event.target.value, 0), 0, 1, 0),
                    )
                  }
                  disabled={isBootstrapping || !isAdmin}
                  type="text"
                  inputMode="decimal"
                  step="0.1"
                  suffix="%"
                  max="100"
                  testId={TESTIDS.simulation.fieldTace}
                />
              )}

              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-lenvia-blue/10 text-lenvia-blue">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-300">
                    Marge intermediaire
                  </span>
                </div>
                <Switch
                  checked={isIntermediaryEnabled}
                  onCheckedChange={(checked) =>
                    updateModeForm(activeMode, "intermediary_enabled", checked)
                  }
                  disabled={isRh || isBootstrapping}
                />
              </div>

              {isIntermediaryEnabled && (
                <Field
                  label={INTERMEDIARY_MARGIN_LABEL}
                  value={activeForm.intermediary_pct}
                  onChange={(event) =>
                    updatePercentModeForm(activeMode, "intermediary_pct", event.target.value)
                  }
                  disabled={isRh || isBootstrapping}
                  type="text"
                  inputMode="decimal"
                  step="0.1"
                  suffix="%"
                  max="100"
                  testId={TESTIDS.simulation.fieldIntermediary}
                />
              )}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <p className="flex-1 text-sm text-slate-400">
                Calcul automatique actif.
              </p>
              <button
                type="button"
                onClick={() => {
                  setForms(applyDefaultsToForms(defaultSettingsRef.current));
                  setComputeErrorMessage("");
                }}
                disabled={isRh || isBootstrapping}
                className="rounded-2xl border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={TESTIDS.simulation.resetButton}
              >
                Reinitialiser
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2">
              {financeCards.map((card) => (
                <KpiStatCard key={card.label} {...card} />
              ))}
            </div>

            {hasIntermediaryImpact && (
              <ChartCard
                title="Impact intermédiaire"
                description="Visualisation de la commission prelevee par l'apporteur."
                icon={Sparkles}
                testId={TESTIDS.simulation.blocIntermediaire}
              >
                <div className="grid gap-4 sm:grid-cols-2" data-testid={TESTIDS.simulation.intermediarySection}>
                  {[
                    {
                      label: "Commission / jour",
                      value: formatSimulationCurrency(activeResult?.commission_per_day),
                      hint: "Prélevé sur TJM client",
                      dataTestId: TESTIDS.simulation.kpiCommissionDay,
                    },
                    {
                      label: "Impact intermédiaire annuel",
                      value: formatEUR0(activeResult?.intermediary_impact_annual),
                      hint: "Manque à gagner annuel",
                      dataTestId: TESTIDS.simulation.kpiIntermediaryAnnual,
                    },
                  ].map((card) => (
                    <KpiStatCard key={card.label} {...card} />
                  ))}
                </div>

                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                  data-testid={TESTIDS.simulation.netSoldSection}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    TJM Net Vendu
                  </p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <p className="text-lg font-semibold text-white">
                      {formatSimulationCurrency(activeResult?.tjm_net_sold)}
                    </p>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Tarif restant après déduction marge intermédiaire (TJM net)
                  </p>
                </div>
              </ChartCard>
            )}

            {isAdmin && (
              <ChartCard
                title="Repartition des couts"
                description="Lecture rapide des couts, de l'intermediaire et de la marge nette."
                icon={GitCompareArrows}
                testId={TESTIDS.simulation.costBreakdownChart}
              >
                {donutData.length > 0 ? (
                  <DonutSplitLayout
                    className="gap-5 xl:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]"
                    chartClassName="h-[280px] max-w-[280px]"
                    chart={(
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={donutData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={70}
                            outerRadius={104}
                            paddingAngle={4}
                          >
                            {donutData.map((entry) => (
                              <Cell key={`${entry.name}-${entry.value}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                    legendClassName="xl:max-h-[280px]"
                    legend={donutData.map((entry) => (
                      <div
                        key={`${entry.name}-legend`}
                        className="flex min-w-0 items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-3">
                          <span
                            className="mt-1 h-3 w-3 flex-none rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="min-w-0 flex-1 break-words text-sm leading-relaxed text-slate-300">
                            {entry.name}
                          </span>
                        </div>
                        <span className="min-w-0 break-words text-right text-sm font-semibold text-white [overflow-wrap:anywhere]">
                          {formatSimulationCurrency(entry.value)}
                        </span>
                      </div>
                    ))}
                  />
                ) : (
                  <div className="flex h-[280px] items-center justify-center rounded-2xl border border-dashed border-white/10 text-sm text-slate-500">
                    Aucun volume a repartir.
                  </div>
                )}
              </ChartCard>
            )}
          </div>
        </section>
      </div>

      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
};

export default SimulationPage;
